import React, { FC, createRef, useState, useEffect, useRef, Fragment } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useHistory, useLocation } from 'react-router-dom'
import type { FormInstance } from 'antd/es/form'
import {
    Space, Form, Input, Button, Select, notification, Popover, DatePicker, InputNumber
} from 'antd'
import {
    // eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
    PlusCircleOutlined, SmileOutlined, CheckCircleOutlined, MinusCircleOutlined
} from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import type { IAppState } from '@/store/reducers'
import type { Descendant } from 'slate'
import { Text } from 'slate'
import DSlate, { DefaultPlugin } from '@dslate/dslate'
import type { DSlateRef } from '@dslate/core'
import { ConfigProvider, defaultConfig } from '@dslate/core'
import { getProjectInfoById, getWorkgroup } from '@/store/actions/tableEdit.action'
import type { IWorkgroupObj, IClassification, ITechYearQuarters, IChangeFields, IEditProjectInfoApply } from '@/store/models/tableEdit.model'
import { editProjectInfoApply } from '@/services/table.service'
import { getErrorStr } from '@/store/sagas/table.saga'
import { requestFail } from '@/store/actions/common.action'
import { ValidateStatus } from '@/store/constant/table.constant'
import PageBreadcrumb from '@/components/pageBreadcrumb'
import NoSpaceInput from '@/components/noSpaceInput'
import { getUuid, ltrim, rtrim } from '@/utils/util'
import _ from 'lodash'
import { getDicClass } from '@/pages/charts'
import Nav from '@/components/nav'

interface IYearSelect {
    value?: number
    validateStatus?: ValidateStatus | undefined
    message?: string
}
interface ISelectFields {
    field: string | null
    label: string
}

// const arr = ['微服务 Micro Service', '数据存储 Data Storage', '自动标注 Automatic Annotation', '边缘计算 Edge Computation', '无钥匙进入及启动系统 PEPS', '差分算法 Differential Algorithm', '以太网协议栈  Ethernet protocol stack', '远程诊断   Remote diagnostic', '测试和评估 Assessment and Test']

// 处理前后空格
export const handleTrim = (arr: any[]): any => {
    const _arr = _.cloneDeep(arr)
    // 第一个节点去掉左空格
    let firstNode = _arr[0]
    while ('type' in firstNode) {
        firstNode = firstNode.children[0]
    }
    firstNode.text = ltrim(firstNode.text)
    // 最后一个节点去掉右空格
    let lastNode = _arr[_arr.length - 1]
    while ('type' in lastNode) {
        lastNode = lastNode.children[lastNode.children.length - 1]
    }
    lastNode.text = rtrim(lastNode.text)
    return _arr
}

export const serialize = (children: any[]): any => {
    return children.map((node: any) => {
        if (Text.isText(node)) {
            return {
                ...node,
                text: node.text.replace(/ /g, '\xa0')
            }
        }
        return {
            ...node,
            children: serialize(node.children)
        }
    })
}

const popMsg = (msg: string) => {
    const args = {
        message: msg,
        description: '',
        icon: <SmileOutlined style={{ color: '#e1931e' }} />,
        duration: 3,
    }
    notification.open(args)
}


// 判断是否连续选择季度，与上一个季度对比
const isContinuity = (cur: string, prev: string) => {
    const arr = ['Q1', 'Q2', 'Q3', 'Q4']
    // 选项的季度位置
    let loc: number = arr.indexOf(cur.substring(3))
    const preLoc: number = arr.indexOf(prev.substring(3))
    const curYear = +(cur.substring(0, 2))
    const prevYear = +(prev.substring(0, 2))
    if (curYear === prevYear) {
        loc--
        if (loc === -1) {
            loc = 0
        }
        if (loc === preLoc) {
            return true
        }
        return false
    } else if (prevYear === curYear - 1) {
        return loc === 0 && preLoc === 3
    } else {
        return false
    }
}
// 判断是否是选了 ON 的后面选项
const isYearQuarterAfter = (year: string, quarter: number, yearSelectsFields: string[], yearSelects: {
        [props: string]: IYearSelect
    }) => {
    const _year = +(year.substring(2))
    // 是否有选 ON 的选项
    const len = yearSelectsFields.length
    for (let i = 0; i < len; i++) {
        const item = yearSelectsFields[i]
        // 找到第一个选 ON 的项，然后判断是否该选项的后续
        if (yearSelects[item].value === 2) {
            const findYear = +item.substring(0, 2)
            const findQuarter = +item.substring(4)
            if (_year < findYear) {
                return false
            }
            if (_year > findYear) {
                return true
            }
            if (_year === findYear) {
                return quarter > findQuarter
            }
        }
    }
    return false
}

const plugs = { ...DefaultPlugin }
// 删除图片工具
Reflect.deleteProperty(plugs, 'ImgPlugin')

const TableEditApply: FC = () => {
    const dispatch = useDispatch()
    const locationObj = useLocation()
    const { t, i18n } = useTranslation()
    const history: any = useHistory()
    const route = useSelector((state: IAppState) => state.router)
    const workgroupList = useSelector((state: IAppState) => state.tableEditData.workgroupList)
    const classification = useSelector((state: IAppState) => state.tableEditData.techClassification)
    const tableEditData = useSelector((state: IAppState) => state.tableEditData.table2EditData)
    const dicClassList = useSelector((state: IAppState) => state.tableEditData.dicClass)

    const formRef = createRef<FormInstance>()
    const editorRef = useRef<DSlateRef>(null)
    const editorRef2 = useRef<DSlateRef>(null)
    const editorRef3 = useRef<DSlateRef>(null)
    const locationSearch = useRef<{
        workGroupId: number | undefined
        projectInfoId: number | undefined
    }>({
        workGroupId: undefined,
        projectInfoId: undefined,
    })
    const { Option } = Select
    const [editorKey, setEditorKey] = useState<string[]>(['1', '2', '3'])
    // 根据 TMT Approval 是否为 PASSED 来决定后续填写项是否必填
    const [isPassed, setIsPassed] = useState(false)
    const [curWorkgroup, setCurWorkgroup] = useState<{
        id: number
        groupName: string
    } | undefined>(undefined)
    // 年修改后的数据
    const backYearsData = useRef<ITechYearQuarters[]>([])
    const [yearsData, setYearsData] = useState<ITechYearQuarters[]>([])
    // 已选择了的要修改的字段名称
    const selectOptions = useRef<ISelectFields[]>([
        // 分类不能修改
        // {
        //     field: 'techClassId', label: t('Classification')
        // }, 
        {
            field: 'researchForms', label: t('researchForms')
        }, {
            field: 'techName', label: t('Technology Name')
        }, {
            field: 'subsidiary', label: t('subsidiary')
        }, {
            field: 'description', label: t('description')
        }, {
            field: 'techLevel', label: t('techLevel')
        }, {
            field: 'priority', label: t('priority')
        }, {
            field: 'techYearQuarters', label: t('year')
        }, {
            field: 'quantitativeIndicators', label: t('quantitativeIndicators')
        }, {
            field: 'tmtapproval', label: t('tmtApproval')
        }, {
            field: 'maturity', label: t('maturity')
        }, {
            field: 'requirementSource', label: t('requirementSource')
        }, {
            field: 'developedBy', label: t('developedBy')
        }, {
            field: 'toolsEquipment', label: t('toolsAndEquipment')
        }, {
            field: 'resourceNeeded', label: t('resourceNeeded')
        }])
    const [fieldsObjs, setFieldsObjs] = useState<{
        options: ISelectFields[] // 修改项的 下拉列表
        selectVal: string | null // 修改字段 值
        disabled?: boolean
        component: any
    }[]>([])
    const [descriptionValue, setDescriptionValue] = useState<Descendant[]>([
        {
            type: 'paragraph',
            children: [{ text: '' }],
        },
    ])
    const [descriptionValue2, setDescriptionValue2] = useState<Descendant[]>([
        {
            type: 'paragraph',
            children: [{ text: '' }],
        },
    ])
    const [descriptionValue3, setDescriptionValue3] = useState<Descendant[]>([
        {
            type: 'paragraph',
            children: [{ text: '' }],
        },
    ])
    // eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
    const [techNameVoli, setTechNameVoli] = useState<{
        help: string
        validateStatus: ValidateStatus | undefined
    }>({
        help: '',
        validateStatus: undefined
    })

    const layout = {
        labelCol: { span: 6 },
        wrapperCol: { span: 10 },
    }
    const tailLayout = {
        wrapperCol: { offset: 6, span: 10 },
    }
    
    const onFinish = async (values: any) => {
        if (Object.keys(values).length === 0) {
            return dispatch(requestFail(t('noChange') + '--' + Math.random()))
        }
        const changeFields: IChangeFields[] = []
        const reqData: IEditProjectInfoApply = {
            auditComments: '',
            auditStatus: 1,
            changeFields,
            techProjectId: locationSearch.current.projectInfoId,
        }
        const _yearsData: ITechYearQuarters[] = []
        let isSample = false
        for (const item of fieldsObjs) {
            const field = item.selectVal || ''
            let after = values[field]
            // 技术名
            if (field === 'techName') {
                after = JSON.stringify({
                    str: editorRef.current?.serialize({ children: serialize(handleTrim(descriptionValue)) }),
                    obj: descriptionValue,
                })
            } else if (field === 'description') {
                // 说明
                after = JSON.stringify({
                    str: editorRef2.current?.serialize({ children: serialize(handleTrim(descriptionValue2)) }),
                    obj: descriptionValue2,
                })
            } else if (field === 'quantitativeIndicators') {
                // 量化指标
                after = JSON.stringify({
                    str: editorRef3.current?.serialize({ children: serialize(handleTrim(descriptionValue3)) }),
                    obj: descriptionValue3,
                })
            } else if (field === 'techYearQuarters') {
                yearsData.forEach(yearDataItem => {
                    const field = `${('' + yearDataItem.year).substring(2)}-Q${yearDataItem.quarter}`
                    if (values[field]) {
                        _yearsData.push(yearDataItem)
                    }
                })
                // 匹配原有id的数据
                _yearsData.forEach((yearDataItem, index) => {
                    tableEditData.techYearQuarters.forEach((item) => {
                        if (yearDataItem.year === item.year && yearDataItem.quarter === item.quarter) {
                            _yearsData[index] = {
                                ...item,
                                status: yearDataItem.status
                            }
                        }
                    })
                })
                after = _yearsData
            } else if (field === 'buStakeholder') {
                after = after.join(',')
            }
            if (JSON.stringify(tableEditData[field]) === JSON.stringify(after)) {
                isSample = true
                break
            }
            changeFields.push({
                field: field === 'tmtapproval' ? 'tMTApproval' : field,
                before: tableEditData[field],
                after
            })
        }
        if (isSample) {
            dispatch(requestFail(t('isSample') + '--' + Math.random()))
            return
        }
        console.log('onFinish: ', values)
        try {
            await editProjectInfoApply(reqData)
            const args = {
                message: t('save') + (i18n.language !== 'zh' ? ' ' : '') + t('success'),
                description: '',
                icon: <CheckCircleOutlined style={{ color: '#08993f' }} />,
                duration: 3,
            }
            notification.open(args)
            if (locationObj.key) {
                history.goBack()
            } else {
                history.replace('/indexPage')
            }
        }catch (error: any) {
            const str = getErrorStr(error)
            dispatch(requestFail(str))
        }
    }
    const onFinishFailed = ({ errorFields }: { errorFields: any}) => {
        console.log('onFinishFailed: ', errorFields)
        if (errorFields.length) {
            popMsg(t('FinishFailed'))
        }
    }
    // eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
    const onReset = () => {
        formRef.current?.resetFields()
        setTechNameVoli({
            help: '',
            validateStatus: undefined
        })
    }

    const groupByClassification = (arr: IClassification[]) => {
        const groupObj: {
            classId: number
            classificationName: string
            workGroup: IWorkgroupObj
        }[] = []
        arr.forEach(item => {
            if (!(item.classId in groupObj)) {
                groupObj[item.classId] = {
                    classId: item.classId,
                    classificationName: item.classificationName,
                    workGroup: item.workGroup
                }
            }
        })
        return groupObj
    }

    // 回显修改前数据, 对比显示修改前数据
    const setOldFormData = (e: string): any => {
        // 获取保存的原数据对象进行回显
        // 技术名
        if (e === 'techName') {
            if (tableEditData.techName) {
                setDescriptionValue(JSON.parse(tableEditData.techName).obj)
                editorKey.splice(0, 1, getUuid(8))
                setEditorKey([...editorKey])
                return {
                    val: JSON.parse(tableEditData.techName).obj,
                    key: editorKey[0]
                }
            }
        }
        // 描述
        if (e === 'description') {
            if (tableEditData.description) {
                setDescriptionValue2(JSON.parse(tableEditData.description).obj)
                editorKey.splice(1, 1, getUuid(8))
                setEditorKey([...editorKey])
                return {
                    val: JSON.parse(tableEditData.description).obj,
                    key: editorKey[1]
                }
            }
        }
        // 量化指标
        if (e === 'quantitativeIndicators') {
            if (tableEditData.quantitativeIndicators) {
                setDescriptionValue3(JSON.parse(tableEditData.quantitativeIndicators).obj)
                editorKey.splice(2, 1, getUuid(8))
                setEditorKey([...editorKey])
            }
            return {
                val: JSON.parse(tableEditData.quantitativeIndicators).obj,
                key: editorKey[2]
            }
        }
    }
    
    // 动态组件
    // 分类
    const techClassComp = (<>
        <Form.Item label={<span className='label'>{(i18n.language !== 'zh' ? ' ' : '') + t('before')}</span>}>
            {tableEditData.classificationName}
        </Form.Item>
        <Form.Item name="techClassId" 
            label={<span className='label'>{(i18n.language !== 'zh' ? ' ' : '') + t('after')}</span>} 
            rules={[{ required: true, message: t('Classification Required') }]}
        >
            <Select allowClear placeholder={t('Classification Placeholder')}>
                {
                    groupByClassification(classification).map(item => (
                        <Option key={item.classId} value={item.classId} title={item.classificationName}>
                            {item.classificationName}
                        </Option>
                    ))
                }
            </Select>
        </Form.Item>
    </>)
    // 研究形式
    const researchFormsComp = (<>
        <Form.Item label={<span className='label'>{(i18n.language !== 'zh' ? ' ' : '') + t('before')}</span>}>
            <span>{
                dicClassList['research form'] 
                && dicClassList['research form'].find(item => item.value === tableEditData.researchForms)?.name
            }</span>
        </Form.Item>
        <Form.Item name="researchForms" 
            label={<span className='label'>{(i18n.language !== 'zh' ? ' ' : '') + t('after')}</span>} 
            rules={[{ required: true, message: t('Classification Required') }]}
        >
            <Select allowClear>
                {
                    dicClassList['research form'] && dicClassList['research form'].map(item => (
                        <Option value={item.value} key={item.value}>{item.name}</Option>
                    ))
                }
            </Select>
        </Form.Item>
    </>)
    // 技术名
    const TechNameComp = (props: {val: Descendant[]}) => {
        const { val } = props
        // 作为验证
        const tempVal = useRef(val)
        const setVal = (e: Descendant[]) => {
            setDescriptionValue(e)
            tempVal.current = e
        }
        const [validateStatusSub, setValidateStatus] = useState<{
            help: string
            validateStatus: ValidateStatus | undefined
        }>({
            help: '',
            validateStatus: undefined
        })
        const setVoliStatus = ({
            help,
            validateStatus
        }: {
            help: string
            validateStatus: ValidateStatus | undefined
        }) => {
            setValidateStatus({
                help,
                validateStatus
            })
            setTechNameVoli({
                help,
                validateStatus
            })
        }
        return (<>
            <Form.Item label={<span className='label'>{(i18n.language !== 'zh' ? ' ' : '') + t('before')}</span>}>
                <div dangerouslySetInnerHTML={{
                    __html: tableEditData.techName ? JSON.parse(tableEditData.techName).str : ''
                }}></div>
            </Form.Item>
            <Form.Item name='techName'
                label={<span className='label'>{(i18n.language !== 'zh' ? ' ' : '') + t('after')}</span>}
                help={validateStatusSub.help}
                validateStatus={validateStatusSub.validateStatus}
                rules={[
                    {
                        required: true
                    },
                    {
                        validator: async () => {
                            if (JSON.stringify(tempVal.current) === '[{"type":"paragraph","children":[{"text":""}]}]') {
                                const helpStr = t('Technology Name Required')
                                setVoliStatus({
                                    help: helpStr,
                                    validateStatus: ValidateStatus.ERROR
                                })
                                return Promise.reject(new Error(helpStr))
                            } else {
                            // 去掉前后空格
                                const editorHtmlValue = editorRef.current?.serialize({ children: serialize(handleTrim(tempVal.current)) })
                                // 获取去掉html后的纯文本
                                const word = editorHtmlValue?.replace(/<[^>]*>/g, '')
                                const errorStr = `${t('maxLenError')} 1000 ${t('lenCharError')}`
                                if (word && word.length > 1000) {
                                    setVoliStatus({
                                        help: errorStr,
                                        validateStatus: ValidateStatus.ERROR
                                    })
                                    return Promise.reject(new Error(errorStr))
                                } else {
                                    setVoliStatus({
                                        help: '',
                                        validateStatus: undefined
                                    })
                                    return Promise.resolve()
                                }
                            }
                        }
                    }
                ]}
            >
                <Input hidden />
                <ConfigProvider
                    value={{
                        ...defaultConfig,
                        plugins: [
                            ...Object.values({
                                ...plugs
                            }), 
                        ],
                    }}
                >
                    <DSlate ref={editorRef} key={editorKey[0]} value={val} onChange={setVal} />
                </ConfigProvider>
            </Form.Item>
        </>)
    }
    // 子技术
    const subsidiaryComp = (<>
        <Form.Item label={<span className='label'>{(i18n.language !== 'zh' ? ' ' : '') + t('before')}</span>}>
            {tableEditData.subsidiary}
        </Form.Item>
        <Form.Item name="subsidiary" 
            label={<span className='label'>{(i18n.language !== 'zh' ? ' ' : '') + t('after')}</span>} 
        >
            <NoSpaceInput allowClear />
        </Form.Item>
    </>)
    // 描述
    const DescriptionComp =  (props: {val: Descendant[]}) => {
        const { val } = props
        // 作为验证
        const tempVal = useRef(val)
        const setVal = (e: Descendant[]) => {
            setDescriptionValue2(e)
            tempVal.current = e
        }
        return (<>
            <Form.Item label={<span className='label'>{(i18n.language !== 'zh' ? ' ' : '') + t('before')}</span>}>
                <div dangerouslySetInnerHTML={{
                    __html: tableEditData.description ? JSON.parse(tableEditData.description).str : ''
                }}></div>
            </Form.Item>
            <Form.Item name="description" 
                label={<span className='label'>{(i18n.language !== 'zh' ? ' ' : '') + t('after')}</span>}
                rules={[
                    {
                        validator: async () => {
                            const editorHtmlValue = editorRef2.current?.serialize({ children: serialize(tempVal.current) })
                            // 获取去掉html后的纯文本
                            const word = editorHtmlValue?.replace(/<[^>]*>/g, '')
                            const errorStr = `${t('maxLenError')} 1000 ${t('lenCharError')}`
                            if (word && word.length > 1000) {
                                return Promise.reject(new Error(errorStr))
                            } else {
                                return Promise.resolve()
                            }
                        }
                    }
                ]}
            >
                <ConfigProvider
                    value={{
                        ...defaultConfig,
                        plugins: [
                            ...Object.values({
                                ...plugs
                            }), 
                        ],
                    }}
                >
                    <DSlate ref={editorRef2} key={editorKey[1]} value={val} onChange={setVal} />
                </ConfigProvider>
            </Form.Item>
        </>)
    }
    // 技术等级
    const techLevelComp = (<>
        <Form.Item label={<span className='label'>{(i18n.language !== 'zh' ? ' ' : '') + t('before')}</span>}>
            <span>{
                [t('core'), t('key'), t('general'), t('normal')][(tableEditData.techLevel as number) - 1]
            }</span>
        </Form.Item>
        <Form.Item name="techLevel" 
            label={<span className='label'>{(i18n.language !== 'zh' ? ' ' : '') + t('after')}</span>} 
        >
            <Select allowClear>
                <Option value={1}>{t('core')}</Option>
                <Option value={2}>{t('key')}</Option>
                <Option value={3}>{t('general')}</Option>
                <Option value={4}>{t('normal')}</Option>
            </Select>
        </Form.Item>
    </>)
    // 优先级
    const priorityComp = (<>
        <Form.Item label={<span className='label'>{(i18n.language !== 'zh' ? ' ' : '') + t('before')}</span>}>
            <span>{
                dicClassList['priority'] 
                && dicClassList['priority'].find(item => item.value === tableEditData.priority)?.name
            }</span>
        </Form.Item>
        <Form.Item name="priority" 
            label={<span className='label'>{(i18n.language !== 'zh' ? ' ' : '') + t('after')}</span>}
        >
            <Select allowClear>
                {
                    dicClassList['priority'] && dicClassList['priority'].map(item => (
                        <Option value={item.value} key={item.value}>{item.name}</Option>
                    ))
                }
            </Select>
        </Form.Item>
    </>)
    // 年
    const YearComp = () => {
        const showYears = useRef<string[]>([])
        const [years, setYears] = useState<string[]>([])
        // 年 选了3个季度后，则不能再选，选了 on 以后，后面的不能再选 plan
        // 缓存每一个选了的数据
        const [yearSelects, setYearSelects] = useState<{
            [props: string]: IYearSelect
        }>({})
        // 选了的年-季度名称
        const [yearSelectsFields, setYearSelectsFields] = useState<string[]>([])
        const [showAddYear, setShowAddYear] = useState<boolean>(false)
        // 保存选中的值
        const yearsDataSub = useRef<ITechYearQuarters[]>(tableEditData.techYearQuarters)
        const setYearsDataSub = (e: ITechYearQuarters[]) => {
            yearsDataSub.current = e
            setYearsData(e)
        }

        const setYearQuarterVoli = ({ techYearQuarters, year, quarter, value,
            yearSelectsFieldsVoli,
            yearSelectsVoli
        }: {
            techYearQuarters?: ITechYearQuarters[]
            year?: string
            quarter?: number
            value?: number
            yearSelectsFieldsVoli?: string[]
            yearSelectsVoli?: {
                [props: string]: IYearSelect
            }
        }) => {
            const _yearSelectsFields: string[] = yearSelectsFieldsVoli ? [...yearSelectsFieldsVoli] : [...yearSelectsFields]
            const _yearSelects = yearSelectsVoli ? yearSelectsVoli : yearSelects
            for (const key in _yearSelects) {
                _yearSelects[key] = {
                    value: _yearSelects[key].value
                }
            }
            const yearQuarterStr = `${year?.substring(2)}-Q${(quarter || 0) + 1}`
            // 编辑时初始带过来
            if (techYearQuarters) {
                for (let i = 0, len = techYearQuarters.length; i < len; i++) {
                    const yearItem = techYearQuarters[i]
                    const _yearQuarterStr = `${String(yearItem.year).substring(2)}-Q${yearItem.quarter}`
                    // 是否连续、超过3个、ON 后面有 PLAN 选项
                    _yearSelects[_yearQuarterStr] = {
                        value: yearItem.status
                    }
                    if (!_yearSelectsFields.includes(_yearQuarterStr)) {
                        _yearSelectsFields.push(_yearQuarterStr)
                    }
                }
            } else if (!value) {
                // 清空时，_yearSelectsFields 中去掉该年季度
                const curIndex = _yearSelectsFields.findIndex(item => item === yearQuarterStr)
                Reflect.deleteProperty(_yearSelects, _yearSelectsFields[curIndex])
                _yearSelectsFields.splice(curIndex, 1)
            } else {
                // 选择切换时
                if (!_yearSelectsFields.includes(yearQuarterStr)) {
                    _yearSelectsFields.push(yearQuarterStr)
                }
                for (let i = 0, len = _yearSelectsFields.length; i < len; i++) {
                    const curYearQuarterStr = _yearSelectsFields[i]
                    if (curYearQuarterStr === yearQuarterStr) {
                        _yearSelects[curYearQuarterStr] = {
                            value
                        }
                    } else {
                        _yearSelects[curYearQuarterStr] = {
                            value: formRef.current?.getFieldValue(curYearQuarterStr)
                        }
                    }
                }
            }
            _yearSelectsFields.sort()
            let firstONIndex = -1
            for (let i = 0, len = _yearSelectsFields.length; i < len; i++) {
                const _yearQuarterStr = _yearSelectsFields[i]
                if (_yearSelects[_yearQuarterStr].value === 2 && firstONIndex === -1) {
                    firstONIndex = i
                }
                if (i > 0) {
                    const prevYearQuarterStr = _yearSelectsFields[i - 1]
                    // 是否超过3个
                    if (i > 2) {
                        _yearSelects[_yearQuarterStr].validateStatus = ValidateStatus.ERROR
                        _yearSelects[_yearQuarterStr].message = t('max3Quarters')
                    }
                    // 判断是否连续，与上一个选项对比
                    if (_yearSelectsFields.length > 2) {
                        if (!isContinuity(_yearQuarterStr, prevYearQuarterStr)) {
                            _yearSelects[_yearQuarterStr].validateStatus = ValidateStatus.ERROR
                            _yearSelects[_yearQuarterStr].message = t('errorMessageContinuity')
                        }
                    }
                    // 判断是否 ON 后面有 PLAN 选项
                    if (firstONIndex !== -1 && i > firstONIndex && _yearSelects[_yearQuarterStr].value === 1) {
                        _yearSelects[_yearQuarterStr].validateStatus = ValidateStatus.ERROR
                        _yearSelects[_yearQuarterStr].message = t('afterOnNotPlay')
                    }
                }
            }
            setYearSelects(_yearSelects)
            setYearSelectsFields(_yearSelectsFields)
            const obj: Record<string, any> = {}
            _yearSelectsFields.forEach(item => {
                obj[item] = _yearSelects[item]
            })
            return obj
        }

        useEffect(() => {
            const formData = { ...tableEditData }
            // 初始化读取年、季度数据，并设置验证状态
            const yearQuarterValue: Record<string, any> = {}
            // 回显选中项
            let yearList: string[] = []
            let _yearSelects: any
            if (tableEditData.techYearQuarters && tableEditData.techYearQuarters.length) {
                tableEditData.techYearQuarters.sort((a, b) => a.quarter - b.quarter)
                tableEditData.techYearQuarters.sort((a, b) => a.year - b.year)
                yearList = Array.from(new Set(tableEditData.techYearQuarters.map(yearItem => '' + yearItem.year)))
                // 设置验证状态
                _yearSelects = setYearQuarterVoli({
                    techYearQuarters: tableEditData.techYearQuarters
                })
                setYearSelects(_yearSelects)
                // 设置年、季度值
                for (const key in _yearSelects) {
                    yearQuarterValue[key] = _yearSelects[key].value
                }
            }
            formRef.current?.setFieldsValue({
                ...formData,
                ...yearQuarterValue
            })
            showYears.current = [...yearList]
            setYears(yearList)
        }, [])

        return (<>
            <Form.Item label={<span className='label'>{t('before')}</span>}>
                {showYears.current.map(year => (
                    <Fragment key={year}>
                        <Form.Item
                            label={<span className='label'>{year + (i18n.language !== 'zh' ? ' ' : '') + t('year')}</span>}
                            className='mb0'
                        >
                        </Form.Item>
                        {tableEditData.techYearQuarters.map(yearItem => {
                            if (yearItem.year === +year) {
                                return (
                                    <Form.Item key={year + '-' + yearItem.quarter}
                                        label={<span className='label'>{`${yearItem.quarter}${i18n.language !== 'zh' ? ' ' : ''}${t('quarter')}`}</span>}
                                    >
                                        {['PLAN', 'ON'][yearItem.status - 1]}
                                    </Form.Item>
                                )
                            }
                        })}
                    </Fragment>
                ))}
            </Form.Item>
            <Form.Item
                label={<span className='label'>{t('after')}</span>}
            >
                {years.map((year, index) => (
                    <Fragment key={year}>
                        <Form.Item name={year}
                            label={<span className='label'>{year + (i18n.language !== 'zh' ? ' ' : '') + t('year')}</span>}
                            className='mb0 txt_right'
                        >
                            {/* 删除按钮 新增的可以删，已有id的读取出来的则不能删除 */}
                            {
                                <MinusCircleOutlined style={{ cursor: 'pointer', color: '#ff4d4f', fontSize: 20 }}
                                    onClick={() => {
                                        yearsDataSub.current = yearsDataSub.current.filter(item => item.year !== +years[index])
                                        years.splice(index, 1)
                                        setYears([...years])
                                        
                                        const _yearSelectsFields = [...yearSelectsFields]
                                        // 清除验证状态
                                        for (let i = 0; i < 4; i++) {
                                            const _yearQuarterStr = `${year.substring(2)}-Q${i + 1}`
                                            const strIndex = _yearSelectsFields.indexOf(_yearQuarterStr)
                                            if (strIndex !== -1) {
                                                _yearSelectsFields.splice(strIndex, 1)
                                            }
                                        }
                                        // 删除年后，重新验证选项是否超过三个，连续，且 ON 后面没有 PLAN
                                        const _yearSelects: {
                                            [props: string]: IYearSelect
                                        } = {}
                                        _yearSelectsFields.forEach(item => {
                                            _yearSelects[item] = {
                                                ...yearSelects[item],
                                                message: '',
                                                validateStatus: undefined
                                            }
                                        })
                                        if (yearsDataSub.current.length > 3) {
                                            // 是否超过3个
                                            const yearItem = yearsDataSub.current[3]
                                            const _yearQuarterStr = `${String(yearItem.year).substring(2)}-Q${yearItem.quarter}`
                                            if (_yearSelects[_yearQuarterStr]) {
                                                _yearSelects[_yearQuarterStr].message = t('max3Quarters')
                                                _yearSelects[_yearQuarterStr].validateStatus = ValidateStatus.ERROR
                                            }
                                        } else {
                                            let firstONIndex = -1
                                            _yearSelectsFields.some((item, index) => {
                                                if (_yearSelects[item].value === 2 && firstONIndex === -1) {
                                                    firstONIndex = index
                                                    return true
                                                }
                                            })
                                            _yearSelectsFields.reduce((prev: string, cur, index) => {
                                                if (prev === '') return cur
                                                // 判断是否连续，与上一个选项对比
                                                if (!isContinuity(cur, prev)) {
                                                    if (_yearSelects[cur]) {
                                                        _yearSelects[cur].message = t('errorMessageContinuity')
                                                        _yearSelects[cur].validateStatus = ValidateStatus.ERROR
                                                    }
                                                }
                                                // 判断是否 ON 后面有 PLAN 选项
                                                if (firstONIndex !== -1 && index > firstONIndex && _yearSelects[cur].value === 1) {
                                                    _yearSelects[cur].validateStatus = ValidateStatus.ERROR
                                                    _yearSelects[cur].message = t('afterOnNotPlay')
                                                }
                                                return cur
                                            }, '')
                                        }
                                        setYearSelectsFields(_yearSelectsFields)
                                        setYearSelects(_yearSelects)
                                    }}
                                />
                            }
                        </Form.Item>
                        {
                            // 季度
                            new Array(4).fill(1).map((_, quarter) => (
                                <Fragment key={`${year}-${quarter + 1}id-${getUuid(8)}`}>
                                    <Form.Item name={`${year.substring(2)}-Q${quarter + 1}id`} hidden><Input /></Form.Item>
                                    <Form.Item name={`${year.substring(2)}-Q${quarter + 1}`}
                                        label={<span className='label'>{`${quarter + 1}${i18n.language !== 'zh' ? ' ' : ''}${t('quarter')}`}</span>}
                                        help={yearSelects[`${year.substring(2)}-Q${quarter + 1}`]?.validateStatus
                                            ? yearSelects[`${year.substring(2)}-Q${quarter + 1}`]?.message
                                            : ''
                                        }
                                        validateStatus={yearSelects[`${year.substring(2)}-Q${quarter + 1}`]?.validateStatus}
                                        rules={[
                                            {
                                                validator: async (rule: any) => {
                                                    if (yearSelects[rule['fullField']]?.validateStatus) {
                                                        return Promise.reject(new Error(yearSelects[rule['fullField']].message as string))
                                                    } else {
                                                        return Promise.resolve()
                                                    }
                                                }
                                            }
                                        ]}
                                    >
                                        <Select allowClear
                                            disabled={yearSelectsFields.length > 2
                                                && yearSelectsFields.indexOf(`${year.substring(2)}-Q${quarter + 1}`) === -1
                                            }
                                            onChange={(value: number) => {
                                                // 查找当前项，在选中列表中的位置
                                                const findIndex = yearsDataSub.current.findIndex(item => item.year === +year &&
                                                    item.quarter === quarter + 1)
                                                const techYearQuarter = [...yearsDataSub.current]
                                                const yearSelectsFieldsNew = [...yearSelectsFields]
                                                const yearSelectsNew: {
                                                    [props: string]: IYearSelect;
                                                } = { ...yearSelects }
                                                const yearQuarterStr = `${year?.substring(2)}-Q${(quarter || 0) + 1}`
                                                if (value) {
                                                    if (findIndex !== -1) {
                                                        // 读取出来的数据被修改
                                                        const oldItem = tableEditData.techYearQuarters.find(item => item.year === +year &&
                                                            item.quarter === quarter + 1)
                                                        techYearQuarter.splice(findIndex, 1, {
                                                            ...oldItem,
                                                            ...yearsDataSub.current[findIndex],
                                                            status: value
                                                        })
                                                    } else {
                                                        techYearQuarter.push({
                                                            quarter: quarter + 1,
                                                            status: value,
                                                            year: +year
                                                        })
                                                        yearSelectsFieldsNew.push(yearQuarterStr)
                                                        yearSelectsNew[yearQuarterStr] = {
                                                            value
                                                        }
                                                    }
                                                } else {
                                                    // 清除时
                                                    if (findIndex !== -1) {
                                                        // 读取出来的数据被修改
                                                        // const oldItem = tableEditData.techYearQuarters.find(item => item.year === +year &&
                                                        //     item.quarter === quarter + 1)
                                                        techYearQuarter.splice(findIndex, 1)
                                                    }
                                                    // 删除掉这个季度
                                                    const yearSelectsCurIndex = yearSelectsFieldsNew.findIndex(item => item === yearQuarterStr)
                                                    yearSelectsFieldsNew.splice(yearSelectsCurIndex, 1)
                                                    setYearSelectsFields([...yearSelectsFieldsNew])
                                                }
                                                setYearsDataSub(techYearQuarter)
                                                // 选择了季度后，验证选项是否超过三个，连续，且 ON 后面没有 PLAN
                                                const _yearSelects = setYearQuarterVoli({ techYearQuarters: yearsDataSub.current, value, year, quarter,
                                                    yearSelectsFieldsVoli: [...yearSelectsFieldsNew],
                                                })
                                                formRef.current?.validateFields(['years'])
                                                setYearSelects(_yearSelects)
                                            }}
                                        >
                                            {/* 如果有一项选中 ON , 则后续不能选 PLAN */}
                                            {/* 还要考虑选了后面的，再在前面选 ON, 此时需要把后面的一是要屏蔽 PLAN, 二是要把 PLAN 全部设为 ON */}
                                            <Option value={1} disabled={isYearQuarterAfter(year, quarter + 1, yearSelectsFields, yearSelects)}>PLAN</Option>
                                            <Option value={2}>ON</Option>
                                        </Select>
                                    </Form.Item>
                                </Fragment>
                            ))
                        }
                    </Fragment>
                ))}
            </Form.Item>
            {/* 添加年 */}
            <Form.Item name='years' label={<span className='label'>{t('add')}{i18n.language !== 'zh' ? ' ' : ''}{t('year')}</span>}
                rules={[
                    {
                        required: true,
                        validator: async () => {
                            const errorStr = t('years Required')
                            if (yearSelectsFields.length === 0) {
                                return Promise.reject(new Error(errorStr))
                            } else {
                                return Promise.resolve()
                            }
                        }
                    }
                ]}
            >
                <Input hidden />
                {/* 没有选择年时，才可以选择，不然只能递增 */}
                {
                    !years.length
                        ? (
                            <Popover visible={showAddYear} trigger="click"
                                onVisibleChange={setShowAddYear}
                                content={
                                    <DatePicker allowClear picker="year"
                                        placeholder={t('Please Select') + (i18n.language !== 'zh' ? ' ' : '') + t('year')}
                                        disabledDate={current => {
                                            let _year = new Date().getFullYear() - 1
                                            if (years.length) {
                                                _year = +years[years.length - 1] + 1
                                            }
                                            return current && current.year() < _year
                                        }}
                                        onChange={(date, dateString) => {
                                            if (date) {
                                                setYears([...years, dateString])
                                                setShowAddYear(false)
                                                const obj: Record<string, any> = {}
                                                for (let i = 0; i < 4; i++) {
                                                    const _yearQuarterStr = `${dateString.substring(2)}-Q${i + 1}`
                                                    obj[_yearQuarterStr] = ''
                                                }
                                                // 清除原有的选择项
                                                const noSelectYear = yearsDataSub.current.filter(item => {
                                                    return (item.year !== +(dateString.substring(0, 4)))
                                                })
                                                setYearsDataSub(noSelectYear)
                                                formRef.current?.setFieldsValue(obj)
                                                console.log('setYearSelects: ', yearSelects)
                                            }
                                        }}
                                    />
                                }
                            >
                                <PlusCircleOutlined style={{ fontSize: 30, cursor: 'pointer', color: '#1890ff' }} />
                            </Popover>
                        )
                        : (
                            <PlusCircleOutlined style={{ fontSize: 30, cursor: 'pointer', color: '#1890ff' }} onClick={() => {
                                let addYear = new Date().getFullYear().toString()
                                // 清除原有的选择项
                                const noSelectYear = yearsDataSub.current.filter(item => {
                                    return (item.year !== +addYear)
                                })
                                setYearsDataSub(noSelectYear)
                                if (years.length === 0) {
                                    setYears([addYear])
                                } else {
                                    addYear = '' + (+years[years.length - 1] + 1)
                                    setYears([...years, addYear])
                                }
                                const obj: Record<string, any> = {}
                                for (let i = 0; i < 4; i++) {
                                    const _yearQuarterStr = `${addYear.substring(2)}-Q${i + 1}`
                                    obj[_yearQuarterStr] = ''
                                }
                                formRef.current?.setFieldsValue(obj)
                            }} />
                        )
                }
            </Form.Item>
        </>)
    }
    // 量化指标
    const QuantitativeIndicatorsComp = (props: {val: Descendant[]}) => {
        const { val } = props
        // 作为验证
        const tempVal = useRef(val)
        const setVal = (e: Descendant[]) => {
            setDescriptionValue3(e)
            tempVal.current = e
        }
        return (<>
            <Form.Item label={<span className='label'>{t('before')}</span>}>
                <div dangerouslySetInnerHTML={{
                    __html: tableEditData.quantitativeIndicators ? JSON.parse(tableEditData.quantitativeIndicators).str : ''
                }}></div>
            </Form.Item>
            <Form.Item name="quantitativeIndicators" 
                label={<span className='label'>{(i18n.language !== 'zh' ? ' ' : '') + t('after')}</span>}
                rules={[
                    {
                        validator: async () => {
                            const editorHtmlValue = editorRef3.current?.serialize({ children: serialize(tempVal.current) })
                            // 获取去掉html后的纯文本
                            const word = editorHtmlValue?.replace(/<[^>]*>/g, '')
                            const errorStr = `${t('maxLenError')} 1000 ${t('lenCharError')}`
                            if (word && word.length > 1000) {
                                return Promise.reject(new Error(errorStr))
                            } else {
                                return Promise.resolve()
                            }
                        }
                    }
                ]}
            >
                <ConfigProvider
                    value={{
                        ...defaultConfig,
                        plugins: [
                            ...Object.values({
                                ...plugs
                            }), 
                        ],
                    }}
                >
                    <DSlate ref={editorRef3} key={editorKey[2]} value={val} onChange={setVal} />
                </ConfigProvider>
            </Form.Item>
        </>)
    }
    // TMT 评审
    const tmtapprovalComp = (<>
        <Form.Item label={<span className='label'>{t('before')}</span>}>
            <span>{
                dicClassList['tmt approval'] 
            && dicClassList['tmt approval'].find(item => item.value === tableEditData.tmtapproval)?.name
            }</span>
        </Form.Item>
        <Form.Item name="tmtapproval" 
            label={<span className='label'>{(i18n.language !== 'zh' ? ' ' : '') + t('after')}</span>}
            rules={[{ required: true, message: t('tmtApproval Required') }]}
        >
            <Select allowClear onChange={e => {
                setIsPassed(e === 'PASSED')
            }}>
                {
                    dicClassList['tmt approval'] && dicClassList['tmt approval'].map(item => (
                        <Option value={item.value} key={item.value}>{item.name}</Option>
                    ))
                }
            </Select>
        </Form.Item>
    </>)
    // 技术状态
    const maturityComp = (<>
        <Form.Item label={<span className='label'>{t('before')}</span>}>
            <span>{
                dicClassList['maturity'] 
            && dicClassList['maturity'].find(item => item.value === tableEditData.maturity)?.name
            }</span>
        </Form.Item>
        <Form.Item name="maturity" 
            label={<span className='label'>{(i18n.language !== 'zh' ? ' ' : '') + t('after')}</span>}
            rules={[{ required: isPassed, message: t('maturity Required') }]}
        >
            <Select allowClear>
                {
                    dicClassList['maturity'] && dicClassList['maturity'].map(item => (
                        <Option value={item.value} key={item.value}>{item.name}</Option>
                    ))
                }
            </Select>
        </Form.Item>
    </>)
    // 需求来源
    const requirementSourceComp = (<>
        <Form.Item label={<span className='label'>{t('before')}</span>}>
            <span>{
                dicClassList['requirement source'] 
            && dicClassList['requirement source'].find(item => item.value === tableEditData.requirementSource)?.name
            }</span>
        </Form.Item>
        <Form.Item name="requirementSource" 
            label={<span className='label'>{(i18n.language !== 'zh' ? ' ' : '') + t('after')}</span>}
            rules={[{ required: isPassed, message: t('requirementSource Required') }]}
        >
            <Select allowClear>
                {
                    dicClassList['requirement source'] && dicClassList['requirement source'].map(item => (
                        <Option value={item.value} key={item.value}>{item.name}</Option>
                    ))
                }
            </Select>
        </Form.Item>
    </>)
    // 课题负责人
    const developedByComp = (<>
        <Form.Item label={<span className='label'>{t('before')}</span>}>
            <span>{tableEditData.developedBy}</span>
        </Form.Item>
        <Form.Item name="developedBy" 
            label={<span className='label'>{(i18n.language !== 'zh' ? ' ' : '') + t('after')}</span>}
            rules={[{ required: isPassed, message: t('developedBy Required') }]}
        >
            <NoSpaceInput allowClear />
        </Form.Item>
    </>)
    // 相关BU
    const buStakeholderComp = (<>
        <Form.Item label={<span className='label'>{t('before')}</span>}>
            <span>{tableEditData.buStakeholder}</span>
        </Form.Item>
        <Form.Item name="buStakeholder" 
            label={<span className='label'>{(i18n.language !== 'zh' ? ' ' : '') + t('after')}</span>}
            rules={[{ required: isPassed, message: t('buStakeholder Required') }]}
        >
            <Select allowClear showSearch
                mode='multiple'
                filterOption={(input, option) => {
                    return ((option?.value ?? '') + '').toLowerCase().includes(input.toLowerCase())
                }}
            >
                {
                    dicClassList['相关BU'] && dicClassList['相关BU'].map(item => (
                        <Option value={item.value} key={item.value}>{item.name}</Option>
                    ))
                }
            </Select>
        </Form.Item>
    </>)
    // 设备需求
    const toolsEquipmentComp = (<>
        <Form.Item label={<span className='label'>{t('before')}</span>}>
            <span>{tableEditData.toolsEquipment}</span>
        </Form.Item>
        <Form.Item name="toolsEquipment" 
            label={<span className='label'>{(i18n.language !== 'zh' ? ' ' : '') + t('after')}</span>}
            rules={[{ required: isPassed, message: t('toolsAndEquipment Required') }]}
        >
            <NoSpaceInput allowClear />
        </Form.Item>
    </>)
    // 资源需求（人月）
    const resourceNeededComp = (<>
        <Form.Item label={<span className='label'>{t('before')}</span>}>
            <span>{tableEditData.resourceNeeded}</span>
        </Form.Item>
        <Form.Item name="resourceNeeded" 
            label={<span className='label'>{(i18n.language !== 'zh' ? ' ' : '') + t('after')}</span>}
            rules={[{ required: isPassed, message: t('resourceNeeded Required') }]}
        >
            <InputNumber style={{ width: '100%' }} min={0} onChange={(e: number | null) => {
                if (e) {
                    formRef.current?.setFieldValue('resourceNeeded', parseInt('' + (e * 10)) / 10)
                }
            }} />
        </Form.Item>
    </>)
    const getNoChange = () => {
        let noChangeObj: any = null
        let n = 0
        const formData = formRef.current?.getFieldsValue()
        for (const key in formData) {
            if (key.includes('select_item_')) {
                const fieldName = formData[key]
                if (fieldName === 'techName') {
                    const descriptionValueOld = JSON.parse(tableEditData[fieldName])?.obj
                    if (JSON.stringify(descriptionValue) === JSON.stringify(descriptionValueOld)) {
                        noChangeObj = {
                            field: fieldName,
                            label: selectOptions.current.find(item => item.field === fieldName)?.label
                        }
                    }
                } else if (fieldName === 'description') {
                    const descriptionValueOld = JSON.parse(tableEditData[fieldName])?.obj
                    if (JSON.stringify(descriptionValue2) === JSON.stringify(descriptionValueOld)) {
                        noChangeObj = {
                            field: fieldName,
                            label: selectOptions.current.find(item => item.field === fieldName)?.label
                        }
                    }
                } else if (fieldName === 'quantitativeIndicators') {
                    const descriptionValueOld = JSON.parse(tableEditData[fieldName])?.obj
                    if (JSON.stringify(descriptionValue3) === JSON.stringify(descriptionValueOld)) {
                        noChangeObj = {
                            field: fieldName,
                            label: selectOptions.current.find(item => item.field === fieldName)?.label
                        }
                    }
                } else if (formData[fieldName] === tableEditData[fieldName]) {
                    noChangeObj = {
                        field: fieldName,
                        label: selectOptions.current.find(item => item.field === fieldName)?.label
                    }
                }
                fieldsObjs[n].disabled = !noChangeObj
                n++
            }
        }
        setFieldsObjs([...fieldsObjs])
        return noChangeObj
    }
    // 没有可选项，不显示添加按钮
    const noOptions = () => {
        const everyFind = selectOptions.current.every(item => {
            const findObj = fieldsObjs.find(subItem => subItem.selectVal === item.field)
            return findObj
        })
        return everyFind
    }

    const [routes, setRoutes] = useState([
        {
            path: '/indexPage',
            breadcrumbName: t('indexPage'),
        },
        {
            path: '/projectInfo',
            breadcrumbName: 'Project Info ' + (i18n.language !== 'zh' ? ' ' : '') + t('list'),
        },
        {
            path: '/projectInfoEdit',
            breadcrumbName: t('editApply'),
        },
    ])

    useEffect(() => {
        console.log('location: ', locationObj)
        const routeParams = new URLSearchParams(location.search)
        const worGroupIdParam = routeParams.get('workGroupId')
        const projectInfoId = routeParams.get('projectId')
        locationSearch.current = {
            workGroupId: +(worGroupIdParam || ''),
            projectInfoId: +(projectInfoId || ''),
        }
        console.log('locationSearch: ', locationSearch.current)

        // 获取字典列表，作为下拉选项
        if (Object.keys(dicClassList).length === 0) {
            // eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars, @typescript-eslint/no-empty-function
            getDicClass(dispatch, (a: boolean): void => {})
        }

        if (!workgroupList.length) {
            // 获取 workgroupList
            dispatch(getWorkgroup())
        }
        // 获取Project Info
        if (locationSearch.current.projectInfoId) {
            // 获取单据信息
            dispatch(getProjectInfoById({ projectInfoId: locationSearch.current.projectInfoId }))
        }
    }, [])
    useEffect(() => {
        const formData = { ...tableEditData }
        console.log('formData: ', formData)
        if (locationSearch.current.workGroupId) {
            const findObj = workgroupList.find(item => item.id === locationSearch.current.workGroupId)
            setCurWorkgroup(findObj)
            const _routes = [...routes]
            if (findObj) {
                _routes[_routes.length - 2].breadcrumbName = findObj.groupName
            }
            setRoutes(_routes)
            formData.workgroupId = locationSearch.current.workGroupId
        }
        
        const _editorKey = [getUuid(8), getUuid(8), getUuid(8)]
        setEditorKey(_editorKey)
        setIsPassed(formData.tmtapproval === 'PASSED')
        // 初始化 workgroupId
        formRef.current?.setFieldsValue({
            ...formData,
            buStakeholder:  formData.buStakeholder ? formData.buStakeholder.split(',') : [],
        })
        setYearsData(formData.techYearQuarters)
    }, [workgroupList, tableEditData])
    useEffect(() => {
        // 不设置值验证会报错
        formRef.current?.setFieldValue('techName', 111)
        formRef.current?.validateFields()
    }, [descriptionValue])
    useEffect(() => {
        console.log('backYearsData: ', backYearsData)
        // 清除之前的，现在没有选的项
        backYearsData.current?.forEach(item => {
            const findObj = yearsData.find(yearObj => yearObj.year === item.year && yearObj.quarter === item.quarter)
            if (!findObj) {
                const field = `${('' + item.year).substring(2)}-Q${item.quarter}`
                formRef.current?.setFieldValue(field, '')
                formRef.current?.setFieldValue(`${field}id`, '')
            }
        })
        backYearsData.current = yearsData
    }, [yearsData])
    return (
        <Space direction="vertical" size={10} style={{ width: '100%' }}>
            <Nav dispatch={dispatch} />
            <PageBreadcrumb routerParam={routes}
                history={history}
                route={route}
                dispatch={dispatch}
            />
            <Form {...layout} ref={formRef} name="control-ref"
                onFinish={onFinish}
                onFinishFailed={onFinishFailed}
                className="edit_table"
            >
                <Form.Item label={<span className='label'>{t('Work Group')}</span>}>
                    { curWorkgroup && curWorkgroup?.groupName }
                </Form.Item>
                <Form.Item label={<span className='label'>{t('Classification')}</span>}>
                    { tableEditData.classificationName }
                </Form.Item>
                <Form.Item label={<span className='label'>{t('PTN')}</span>}>
                    { tableEditData.techPtn }
                </Form.Item>
                {fieldsObjs.map((selectField, index) => (<Fragment key={'select_item_' + index}>
                    <Form.Item
                        label={t('changeField')}
                        className='form_item_hor'
                        name={'select_item_' + index}
                        rules={[{ required: true, message: t('changeField Required') }]}
                    >
                        <Select placeholder={t('Please Select') + (i18n.language !== 'zh' ? ' ' : '') + t('changeField')}
                            filterOption={false}
                            disabled={selectField.disabled}
                            onChange={e => {
                                console.log('e: ', e)
                                let oldDescription
                                fieldsObjs[index].selectVal = e
                                switch (e) {
                                    case 'techClassId':
                                        fieldsObjs[index].component = techClassComp
                                        break
                                    case 'researchForms':
                                        fieldsObjs[index].component = researchFormsComp
                                        break
                                    case 'techName':
                                        oldDescription = setOldFormData(e)
                                        if (oldDescription) {
                                            fieldsObjs[index].component = <TechNameComp val={oldDescription.val} key={oldDescription.key} />
                                        }
                                        break
                                    case 'subsidiary':
                                        fieldsObjs[index].component = subsidiaryComp
                                        break
                                    case 'description':
                                        oldDescription = setOldFormData(e)
                                        if (oldDescription) {
                                            fieldsObjs[index].component = <DescriptionComp val={oldDescription.val} key={oldDescription.key} />
                                        }
                                        break
                                    case 'techLevel':
                                        fieldsObjs[index].component = techLevelComp
                                        break
                                    case 'priority':
                                        fieldsObjs[index].component = priorityComp
                                        break
                                    case 'techYearQuarters':
                                        fieldsObjs[index].component = <YearComp/>
                                        break
                                    case 'quantitativeIndicators':
                                        oldDescription = setOldFormData(e)
                                        if (oldDescription) {
                                            fieldsObjs[index].component = <QuantitativeIndicatorsComp val={oldDescription.val} key={oldDescription.key} />
                                        }
                                        break
                                    case 'tmtapproval':
                                        fieldsObjs[index].component = tmtapprovalComp
                                        break
                                    case 'maturity':
                                        fieldsObjs[index].component = maturityComp
                                        break
                                    case 'requirementSource':
                                        fieldsObjs[index].component = requirementSourceComp
                                        break
                                    case 'developedBy':
                                        fieldsObjs[index].component = developedByComp
                                        break
                                    case 'buStakeholder':
                                        fieldsObjs[index].component = buStakeholderComp
                                        break
                                    case 'toolsEquipment':
                                        fieldsObjs[index].component = toolsEquipmentComp
                                        break
                                    case 'resourceNeeded':
                                        fieldsObjs[index].component = resourceNeededComp
                                        break
                                    default:
                                }
                                console.log('fieldsObjs: ', fieldsObjs)
                                setFieldsObjs([...fieldsObjs])
                            }}
                        >
                            {selectField.options.map(item => (
                                <Option key={item.field} value={item.field} title={item.label}>
                                    {item.label}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>
                    {selectField.component}
                </Fragment>))}
                <Form.Item
                    label={<span className='label'>{t('addEdit')}</span>}
                >
                    <PlusCircleOutlined
                        style={{ fontSize: 30,
                            color: noOptions() ? '#999' : '#1890ff',
                            cursor: noOptions() ? 'default' : 'pointer'
                        }}
                        onClick={() => {
                            console.log('formRef.current.getFieldsValue: ', formRef.current?.getFieldsValue())
                            if (noOptions()) {
                                return
                            }
                            if (fieldsObjs.length) {
                                // 判断是否有未选的，如果有，则不用再添加
                                const hasEmpty = fieldsObjs.find(item => !item.selectVal)
                                if (hasEmpty) {
                                    return dispatch(requestFail(t('changeField Required'))) + '--' + Math.random()
                                }

                                // 验证是否未做修改，如果未修改，不能新增
                                const hasNoChange = getNoChange()
                                if (hasNoChange) {
                                    return dispatch(requestFail(
                                        (hasNoChange.label ? ('"' + hasNoChange.label + '"') : t('you have field'))
                                        + (i18n.language !== 'zh' ? ' ' : '')
                                        + t('isNoChange') + '--' + Math.random()
                                    ))
                                }

                                const prevSelect = fieldsObjs[fieldsObjs.length - 1]
                                const prevSelectOptions = prevSelect.options
                                // 在上一条基础上去掉被选中项
                                const options = prevSelectOptions.filter(item => item.field !== prevSelect.selectVal)
                                fieldsObjs.push({
                                    options,
                                    selectVal: null,
                                    component: <></>
                                })
                            } else {
                                fieldsObjs.push({
                                    options: selectOptions.current,
                                    selectVal: null,
                                    component: <></>
                                })
                            }
                            
                            setFieldsObjs([...fieldsObjs])
                        }}
                    />
                </Form.Item>
                <Form.Item {...tailLayout} style={{ textAlign: 'center' }}>
                    <Space size={20}>
                        <Button type="primary" htmlType="submit">{t('submit')}</Button>
                        {locationObj.pathname.includes('Add') && <Button htmlType="button" onClick={onReset}>{t('reset')}</Button>}
                    </Space>
                </Form.Item>
            </Form>
        </Space>
    )
}

export default TableEditApply
